import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe, TrendingUp, Wrench, Sparkles, ArrowUpRight, ArrowRight, Star, Zap,
  Target, Search, MousePointerClick, Check, ShieldCheck, MessagesSquare, BadgeDollarSign, Clock,
  PenLine, Download,
} from "lucide-react";
import { MaskedLines, MaskedWords, Reveal, Marquee } from "@/components/motion/primitives";
import { SectionTitle } from "@/components/site/kit";
import SEO from "@/components/site/SEO";
import { useCurrency } from "@/context/CurrencyContext";
import { getTestimonials, getFaqs, getPricingConfig } from "@/lib/api";


const VALUE_PROPS = [
  { icon: Target, title: "Built for Your Business", body: "Custom websites designed around your goals and your customers — never generic templates with your logo dropped in." },
  { icon: Search, title: "Built to Get Found", body: "SEO foundations, mobile optimization, fast performance, and a search-friendly structure included in every build." },
  { icon: MousePointerClick, title: "Built to Convert", body: "Clear calls to action, contact forms, booking options, and conversion-focused layouts that turn visits into revenue." },
];

const WHY = [
  { icon: MessagesSquare, title: "You work with me directly", body: "The person you talk to is the person building your website. No account managers, no telephone game." },
  { icon: Globe, title: "Remote, worldwide", body: "Based in France, serving businesses worldwide. Async communication means progress happens around the clock." },
  { icon: BadgeDollarSign, title: "Transparent pricing", body: "Published starting prices and a 60-second instant estimate. No sales call required to know what things cost." },
  { icon: ShieldCheck, title: "Custom solutions", body: "Your website is designed around your business goals — not a template with your name on it." },
  { icon: Zap, title: "Modern technology", body: "Fast, secure, mobile-first builds with proper SEO structure, analytics, and clean code." },
  { icon: Clock, title: "Ongoing support", body: "Optional care plans keep your site fast, secure, and updated long after launch — cancel anytime." },
];

const HERO_PROMPT_IDEAS = [
  { label: "Online store", prompt: "A modern online store for my handmade candle brand with product pages and a checkout" },
  { label: "Restaurant", prompt: "A website for my family restaurant with the menu, a photo gallery and table reservations" },
  { label: "Coach", prompt: "A personal coaching website with my services, client testimonials and a session booking form" },
  { label: "Portfolio", prompt: "A clean portfolio website for a freelance photographer with galleries and a contact form" },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [heroPrompt, setHeroPrompt] = useState("");
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [pricing, setPricing] = useState(null);
  const heroRef = useRef(null);
  const heroInputRef = useRef(null);
  const { fmt } = useCurrency();


  useEffect(() => {
    getTestimonials().then((d) => setTestimonials(d.slice(0, 3))).catch(() => {});
    getFaqs().then((d) => setFaqs(d.slice(0, 5))).catch(() => {});
    getPricingConfig().then(setPricing).catch(() => {});
  }, []);

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Julius Mason — Web Design & Development",
    description: "Professional websites for businesses that want more customers. Custom web design, development, SEO, and website care for small businesses worldwide.",
    url: "https://juliusmason.com",
    founder: { "@type": "Person", name: "Julius Mason" },
    areaServed: "Worldwide",
    priceRange: "$499 - $5000+",
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Professional Websites for Businesses That Want More Customers | Julius Mason"
        description="Custom websites for small businesses, entrepreneurs, startups, and organizations worldwide. Get an instant estimate and start your project online — no sales call needed."
        keywords="web design, website developer, small business website, professional website design, website cost, hire web developer"
        path="/"
        lang="en"
        schema={professionalServiceSchema}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden dot-grid pt-24 md:pt-28">
        <div className="mx-auto max-w-[1600px] w-full px-6 md:px-12 pb-16 md:pb-24">
          {/* The guide mascot (bottom-left) plays the host — no big portrait needed. */}
          <div className="mt-4 md:mt-8 max-w-4xl mx-auto text-center">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.7 }} className="overline mb-5">
              Julius Mason — Web Design &amp; Development
            </motion.p>
            <MaskedLines
              lines={["Professional Websites That", "Help Businesses Grow."]}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.98] tracking-tight text-foreground"
              delay={0.3}
            />
            <Reveal delay={0.8}>
              <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Online stores, booking systems, portfolios, full business sites — describe what you
                need and watch your website take shape in seconds. Design, hosting, SEO and
                care: all handled for you.
              </p>
            </Reveal>
            <Reveal delay={0.95} className="mt-8">
              <form
                data-testid="hero-build-form"
                className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (heroPrompt.trim().length < 10) return;
                  navigate("/build", { state: { description: heroPrompt.trim(), autorun: true } });
                }}
              >
                <input
                  data-testid="hero-build-input"
                  ref={heroInputRef}
                  value={heroPrompt}
                  onChange={(e) => setHeroPrompt(e.target.value)}
                  placeholder="e.g. A modern online store for my candle brand…"
                  className="flex-1 border border-border bg-background rounded-full px-6 py-4 text-base outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/60 soft-shadow"
                />
                <button
                  type="submit"
                  data-testid="hero-build-submit"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-4 font-semibold hover:bg-primary/90 active:scale-95 transition soft-shadow whitespace-nowrap"
                >
                  <Sparkles className="w-4 h-4" /> Build my website
                </button>
              </form>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-testid="hero-prompt-chips">
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground mr-1">Try:</span>
                {HERO_PROMPT_IDEAS.map((idea) => (
                  <button
                    key={idea.label}
                    type="button"
                    data-testid={`hero-chip-${idea.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => { setHeroPrompt(idea.prompt); heroInputRef.current?.focus(); }}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary active:scale-95 transition-colors"
                  >
                    {idea.label}
                  </button>
                ))}
              </div>
            </Reveal>
            <Reveal delay={1.05}>
              <p className="mt-6 font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="hero-trust-line">
                Serving clients worldwide · Remote development · Transparent pricing
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-16 md:py-24 border-y border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-3 gap-10">
          {VALUE_PROPS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="border-t-2 border-primary pt-6" >
              <div data-testid={`value-prop-${i}`}>
                <v.icon className="w-8 h-8 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3">{v.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INSTANT QUOTE — the centerpiece */}
      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32" data-testid="how-it-works">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="max-w-3xl mb-14">
            <SectionTitle
              overline="How It Works"
              title={<>Three Steps.<br />Zero Upfront.</>}
              className="mb-4"
            />
            <p className="text-muted-foreground text-lg leading-relaxed">
              No sales calls, no deposits, no mystery timelines — a clear process that
              starts the moment you describe your idea.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", title: "Describe it", body: "Tell me what your business needs in your own words and watch your website take shape live — free, no account needed." },
              { n: "02", title: "Refine it", body: "We adjust the details together until it's exactly right — layout, colors, features, everything." },
              { n: "03", title: "Launch it", body: "I build, host and care for the real thing. Everything is free for 30 days — then one flat monthly rate covers build, hosting and care." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1} className="border-t-2 border-primary pt-6">
                <div data-testid={`how-step-${i + 1}`}>
                  <p className="font-mono text-sm text-primary tracking-widest mb-3">{s.n}</p>
                  <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/build" data-testid="how-build-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
              <Sparkles className="w-4 h-4" /> Build my website
            </Link>
            <Link to="/pricing" data-testid="how-pricing-link" className="font-mono text-sm uppercase tracking-widest text-primary link-underline">
              See pricing & plans →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee className="py-8 border-y border-border bg-secondary/30">
        <div className="flex items-center gap-10 pr-10">
          {["Business Websites", "E-commerce", "Landing Pages", "Booking Systems", "SEO", "Website Care", "Web Apps"].map((s, i) => (
            <span key={i} className="font-display text-3xl md:text-5xl text-stroke whitespace-nowrap flex items-center gap-10">
              {s} <Star className="w-6 h-6 text-primary flex-shrink-0" fill="currentColor" />
            </span>
          ))}
        </div>
      </Marquee>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionTitle overline="Services" title={<>One partner for your<br />entire online presence.</>} className="mb-0" />
            <Link to="/services" className="font-mono text-sm uppercase tracking-widest text-primary link-underline self-start md:self-auto">All Services →</Link>
          </div>
          <div className="grid lg:grid-cols-12 gap-4">
            {[
              { icon: Globe, title: "Web Design & Development", desc: "Business websites, landing pages, e-commerce, booking sites, and custom web applications.", to: "/services/web-design", span: "lg:col-span-8", primary: true },
              { icon: TrendingUp, title: "SEO & Search Visibility", desc: "Technical, on-page, and local SEO plus Google Business optimization.", to: "/services/seo-marketing", span: "lg:col-span-4" },
              { icon: Wrench, title: "Website Care", desc: "Hosting, security, backups, updates — first 30 days free, then from $20/mo.", to: "/services/website-care", span: "lg:col-span-4" },
              { icon: Sparkles, title: "Digital Growth", desc: "Conversion optimization, lead generation, automation, and smart integrations.", to: "/services/digital-growth", span: "lg:col-span-8" },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} className={c.span}>
                <Link
                  to={c.to}
                  data-testid={`service-card-${i}`}
                  className={`group relative flex flex-col justify-between h-full min-h-[220px] p-8 border transition-colors overflow-hidden ${c.primary ? "border-primary/40 bg-primary/5 hover:bg-primary/10" : "border-border bg-card hover:bg-secondary/40"}`}
                >
                  <div className="flex items-start justify-between">
                    <c.icon className="w-9 h-9 text-primary" strokeWidth={1.4} />
                    {c.primary && <span className="font-mono text-[10px] uppercase tracking-widest text-primary border border-primary/40 px-2 py-1">Primary service</span>}
                  </div>
                  <div className="mt-14">
                    <h3 className="font-display text-2xl md:text-3xl leading-tight max-w-[85%]">{c.title}</h3>
                    <p className="text-sm text-muted-foreground mt-3 max-w-md leading-relaxed">{c.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                      Explore <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES PREVIEW */}
      {pricing && (
        <section className="py-24 md:py-32 border-t border-border bg-secondary/20" data-testid="home-packages">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <SectionTitle overline="Transparent Pricing" title={<>Know the price<br />before you commit.</>} className="mb-0" />
              <Link to="/pricing" data-testid="home-pricing-link" className="font-mono text-sm uppercase tracking-widest text-primary link-underline self-start md:self-auto">Full Pricing &amp; Care Plans →</Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {pricing.packages.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.07}>
                  <div data-testid={`home-package-${p.id}`} className={`flex flex-col h-full border p-7 ${p.featured ? "border-primary bg-card soft-shadow-lg" : "border-border bg-card"}`}>
                    {p.featured && <span className="font-mono text-[10px] uppercase tracking-widest text-primary mb-3">Most popular</span>}
                    <h3 className="font-display text-2xl">{p.name}</h3>
                    <p className="mt-2 font-display text-3xl text-primary">From {fmt(p.price_from)}</p>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{p.tagline}</p>
                    <ul className="mt-5 space-y-2 text-sm">
                      {p.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /> {f}</li>
                      ))}
                    </ul>
                    <Link
                      to={`/start-project?package=${p.id}`}
                      data-testid={`home-package-cta-${p.id}`}
                      className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition active:scale-95 ${p.featured ? "bg-primary text-white hover:bg-primary/90" : "border border-border hover:border-primary hover:text-primary"}`}
                    >
                      Choose This Package <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY WORK WITH JULIUS */}
      <section className="py-24 md:py-32 border-t border-border" data-testid="home-why">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Why Work With Julius?" title={<>One person who understands<br />both technology and business.</>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07} className="border-t border-primary pt-6">
                <w.icon className="w-7 h-7 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-xl mb-2">{w.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AVENO BRAND TEASER */}
      <section className="py-24 md:py-32 border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden border border-border bg-[#FAF7F2]">
              <img src="/shop-images/aveno-hero.png" alt="AVENO everyday goods flat-lay" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <img src="/shop-images/aveno-logo.png" alt="AVENO logo" className="w-14 h-14 object-contain mb-5" />
            <SectionTitle overline="My Own Brand" title="AVENO — everyday goods, made properly." className="mb-6" />
            <p className="text-muted-foreground leading-relaxed mb-8 max-w-lg">
              Organic apparel, drinkware, stationery, home goods and a kids' line — designed by me,
              modeled by my family, shipped from my own store.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/aveno" data-testid="home-aveno-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
                The brand story <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop" data-testid="home-shop-cta" className="inline-flex items-center gap-2 border border-border rounded-full px-7 py-3.5 font-medium hover:border-primary hover:text-primary active:scale-95 transition">
                Shop AVENO
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-32 border-t border-border">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10">
            <SectionTitle overline="Kind Words" title="What clients say" />
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08} className="border border-border p-8 flex flex-col justify-between bg-card">
                  <div>
                    <div className="flex gap-1 text-primary mb-5">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4" fill="currentColor" />)}</div>
                    <p className="text-lg leading-relaxed">"{t.quote}"</p>
                  </div>
                  <div className="mt-8 pt-6 border-t border-border">
                    <p className="font-display text-lg">{t.author}</p>
                    <p className="text-sm text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ""}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ TEASER */}
      {faqs.length > 0 && (
        <section className="py-24 md:py-32 border-t border-border bg-secondary/20" data-testid="home-faq">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionTitle overline="Questions, Answered" title={<>Everything you're<br />wondering.</>} className="mb-6" />
              <p className="text-muted-foreground leading-relaxed">Pricing, timelines, countries, deposits, maintenance — the honest answers, up front.</p>
              <Link to="/pricing#faq" className="mt-6 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-primary link-underline">All FAQs <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="lg:col-span-7 space-y-1">
              {faqs.map((f, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <details className="group border-b border-border py-5" data-testid={`home-faq-${i}`}>
                    <summary className="flex items-center justify-between cursor-pointer font-medium text-lg list-none">
                      {f.q}
                      <ArrowRight className="w-4 h-4 text-primary rotate-90 group-open:-rotate-90 transition-transform flex-shrink-0 ml-4" />
                    </summary>
                    <p className="mt-4 text-muted-foreground leading-relaxed pr-8">{f.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EXPLORE — Tools, Blog, Playbook, AI Builder keep driving traffic & SEO */}
      <section className="py-24 md:py-32 border-t border-border" data-testid="home-resources">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="mb-14">
            <SectionTitle overline="Free to Explore" title={<>Try, learn,<br />and build — free.</>} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/build", icon: Sparkles, label: "Website Previewer", desc: "Describe your project and watch a working preview generate live." },
              { to: "/tools", icon: Wrench, label: "Free Tools", desc: "25+ browser tools: QR codes, invoices, SEO helpers, PDF utilities." },
              { to: "/blog", icon: PenLine, label: "The Blog", desc: "Practical guides on websites, pricing, SEO, and growing online." },
              { to: "/free-playbook", icon: Download, label: "The Playbook", desc: "Free 10-page website planning workbook — yours to keep." },
            ].map(({ to, icon: Icon, label, desc }, i) => (
              <Reveal key={to} delay={i * 0.04}>
                <Link to={to} data-testid={`home-resource-${label.toLowerCase().replace(/\s+/g, "-")}`} className="group flex flex-col h-full border border-border bg-background p-6 hover-lift">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-10 h-10 grid place-items-center bg-primary/10 text-primary rounded-lg"><Icon className="w-5 h-5" /></div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <h3 className="font-display text-2xl leading-tight group-hover:text-primary transition-colors">{label}</h3>
                  <p className="text-sm text-muted-foreground mt-2 flex-1">{desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-40 bg-primary text-primary-foreground relative overflow-hidden" data-testid="home-final-cta">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 relative z-10">
          <MaskedWords text="Ready to Build a Better Website?" as="h2" className="font-display text-5xl md:text-8xl leading-[0.9] max-w-5xl" />
          <p className="mt-8 text-xl text-primary-foreground/85 max-w-2xl">
            Tell me what your business needs. Get an instant estimate and start your project online.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/pricing" data-testid="final-quote-cta" className="inline-flex items-center gap-2 bg-background text-primary px-8 py-4 font-semibold hover:bg-background/90 transition-colors active:scale-95">
              See Pricing & Plans <Zap className="w-5 h-5" />
            </Link>
            <Link to="/free-website-audit" data-testid="final-audit-cta" className="inline-flex items-center gap-2 border border-primary-foreground/40 px-8 py-4 font-semibold hover:bg-primary-foreground/10 transition-colors active:scale-95">
              Get a Free Website Audit <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="mt-12 font-mono text-xs uppercase tracking-widest text-primary-foreground/70">
            Based in France · Serving businesses worldwide
          </p>
        </div>
        <div className="absolute -bottom-20 -right-10 font-display text-[20rem] leading-none text-primary-foreground/10 select-none pointer-events-none hidden lg:block">JM</div>
      </section>
    </div>
  );
}
